import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import { useAuth } from '../../hooks/useAuth';
import {
  MagazineInvoice,
  deleteInvoice,
  getInvoice,
  updateInvoice,
  updateInvoiceStatus,
} from '../../api/invoiceService';
import { syncInvoiceToQuickBooks } from '../../api/quickbooksService';
import { qbSyncBadgeClass, qbSyncLabel } from '../../util/qbSyncStatus';

const linkButtonClass =
  'inline-block rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white no-underline hover:bg-torch-primary-dark';

const statusClass: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-50 text-blue-700',
  paid: 'bg-green-50 text-green-700',
  overdue: 'bg-red-50 text-red-700',
};

const formatMoney = (amount: string | number, currency: string) => {
  const n = Number(amount);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(n);
};

const vatStatusLabel = (
  valid: boolean | null | undefined,
  validatedAt?: string | null,
  vatNumber?: string | null
) => {
  if (!vatNumber) return 'No VAT number on file';
  if (valid === true) {
    return `Valid (${vatNumber})${validatedAt ? ` · checked ${new Date(validatedAt).toLocaleDateString()}` : ''}`;
  }
  if (valid === false) {
    return `Invalid (${vatNumber})${validatedAt ? ` · checked ${new Date(validatedAt).toLocaleDateString()}` : ''}`;
  }
  return `Not validated (${vatNumber})`;
};

const vatStatusClass = (valid: boolean | null | undefined, vatNumber?: string | null) => {
  if (!vatNumber) return 'bg-gray-100 text-gray-700';
  if (valid === true) return 'bg-green-50 text-green-800';
  if (valid === false) return 'bg-red-50 text-red-800';
  return 'bg-amber-50 text-amber-800';
};

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user, can } = useAuth();
  const canManageQuickBooks = can('quickbooks.manage');
  const [invoice, setInvoice] = useState<MagazineInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editing, setEditing] = useState(false);
  const [syncingQb, setSyncingQb] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getInvoice(id);
      setInvoice(data);
      setEditNotes(data.notes || '');
      setEditDueDate(data.due_date || '');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Invoice not found.');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const canUpdate =
    isAdmin || (invoice && user?.id && invoice.sales_rep_id === user.id);

  const canEdit = canUpdate && (isAdmin || invoice?.status === 'draft');

  const handleStatus = async (status: string) => {
    if (!invoice) return;
    setUpdating(true);
    setError(null);
    try {
      const updated = await updateInvoiceStatus(invoice.id, status);
      setInvoice(updated);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update status.');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!invoice) return;
    setUpdating(true);
    setError(null);
    try {
      const updated = await updateInvoice(invoice.id, {
        notes: editNotes,
        due_date: editDueDate || undefined,
      });
      setInvoice(updated);
      setEditing(false);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save changes.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!invoice || !isAdmin || !window.confirm('Delete this invoice?')) return;
    await deleteInvoice(invoice.id);
    navigate('/invoices');
  };

  const handleSyncQuickBooks = async () => {
    if (!invoice) return;
    setSyncingQb(true);
    setError(null);
    try {
      await syncInvoiceToQuickBooks(invoice.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'QuickBooks sync failed.');
    } finally {
      setSyncingQb(false);
    }
  };

  const slot = invoice?.magazine_booking?.ad_slot;
  const proposalLines = invoice?.proposal?.line_items || [];

  return (
    <Layout>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {invoice?.invoice_number || 'Invoice'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">{invoice?.company?.name || 'Magazine invoice'}</p>
        </div>
        <div className="flex gap-2">
          {invoice?.proposal_id && (
            <Link
              to={`/magazine/proposals/${invoice.proposal_id}`}
              className="inline-block rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 no-underline hover:bg-gray-50"
            >
              View proposal
            </Link>
          )}
          <Link to="/invoices" className={linkButtonClass}>
            Back to invoices
          </Link>
        </div>
      </div>

      {loading ? (
        <PageCard><p className="text-sm text-gray-500">Loading...</p></PageCard>
      ) : !invoice ? (
        <PageCard><p className="text-sm text-gray-500">{error || 'Not found.'}</p></PageCard>
      ) : (
        <>
          <PageCard className="mb-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="text-sm font-medium text-gray-600">Status</div>
                <div className="mt-1">
                  <span className={`rounded px-2 py-0.5 text-sm font-medium capitalize ${statusClass[invoice.status] || ''}`}>
                    {invoice.status}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600">Amount</div>
                <div className="mt-1 text-sm text-gray-900">
                  {formatMoney(invoice.amount, invoice.currency)}
                </div>
              </div>
              {invoice.proposal_id ? (
                <div className="sm:col-span-2">
                  <div className="text-sm font-medium text-gray-600">Proposal line items</div>
                  <ul className="mt-1 list-inside list-disc text-sm text-gray-700">
                    {proposalLines.length ? proposalLines.map((line, i) => (
                      <li key={i}>
                        {line.ad_slot?.issue?.publication?.name || '—'} — {line.label}
                      </li>
                    )) : (
                      <li>Proposal #{invoice.proposal_id}</li>
                    )}
                  </ul>
                </div>
              ) : (
                <>
                  <div>
                    <div className="text-sm font-medium text-gray-600">Publication / issue</div>
                    <div className="mt-1 text-sm text-gray-900">
                      {slot?.issue?.publication?.name || '—'}
                      <br />
                      <span className="text-gray-500">{slot?.issue?.title || '—'}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-600">Ad slot</div>
                    <div className="mt-1 text-sm text-gray-900">{slot?.label || '—'}</div>
                  </div>
                </>
              )}
              <div>
                <div className="text-sm font-medium text-gray-600">Sales rep</div>
                <div className="mt-1 text-sm text-gray-900">
                  {invoice.sales_rep?.username || invoice.sales_rep?.email || '—'}
                </div>
              </div>
              {invoice.company && (
                <div>
                  <div className="text-sm font-medium text-gray-600">Company VAT</div>
                  <div className="mt-1">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-sm font-medium ${vatStatusClass(
                        invoice.company.vat_valid,
                        invoice.company.vat_number
                      )}`}
                    >
                      {vatStatusLabel(
                        invoice.company.vat_valid,
                        invoice.company.vat_validated_at,
                        invoice.company.vat_number
                      )}
                    </span>
                    {invoice.company.vat_registered_name && (
                      <div className="mt-1 text-sm text-gray-600">
                        {invoice.company.vat_registered_name}
                      </div>
                    )}
                    {invoice.company.id && (
                      <Link
                        to={`/companys/${invoice.company.id}/overview`}
                        className="mt-1 inline-block text-sm text-torch-primary hover:underline"
                      >
                        View company
                      </Link>
                    )}
                  </div>
                </div>
              )}
              <div>
                <div className="text-sm font-medium text-gray-600">QuickBooks</div>
                <div className="mt-1">
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-sm font-medium ${qbSyncBadgeClass(
                      invoice.qb_sync_status
                    )}`}
                  >
                    {qbSyncLabel(invoice, 'invoice')}
                  </span>
                  {canManageQuickBooks && (
                    <button
                      type="button"
                      onClick={handleSyncQuickBooks}
                      disabled={syncingQb}
                      className="ml-2 text-sm text-torch-primary hover:underline disabled:opacity-50"
                    >
                      {syncingQb ? 'Syncing…' : 'Sync now'}
                    </button>
                  )}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600">Due date</div>
                <div className="mt-1 text-sm text-gray-900">
                  {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '—'}
                </div>
              </div>
              {invoice.contract && (
                <div>
                  <div className="text-sm font-medium text-gray-600">Linked contract</div>
                  <div className="mt-1">
                    <Link to={`/contracts/${invoice.contract.id}`} className="text-sm text-torch-primary hover:underline">
                      Contract #{invoice.contract.id} ({invoice.contract.status})
                    </Link>
                  </div>
                </div>
              )}
              <div>
                <div className="text-sm font-medium text-gray-600">Timestamps</div>
                <div className="mt-1 text-sm text-gray-600">
                  Created: {invoice.createdAt ? new Date(invoice.createdAt).toLocaleString() : '—'}
                  {invoice.sent_at && (
                    <>
                      <br />
                      Sent: {new Date(invoice.sent_at).toLocaleString()}
                    </>
                  )}
                  {invoice.paid_at && (
                    <>
                      <br />
                      Paid: {new Date(invoice.paid_at).toLocaleString()}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 border-t border-gray-200 pt-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">Notes</div>
                {canEdit && !editing && (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="text-sm text-torch-primary hover:underline"
                  >
                    Edit
                  </button>
                )}
              </div>
              {editing ? (
                <div>
                  <textarea
                    rows={3}
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                    disabled={updating}
                  />
                  <div className="mt-2 flex flex-wrap gap-3">
                    <label className="text-sm text-gray-600">
                      Due date
                      <input
                        type="date"
                        value={editDueDate}
                        onChange={(e) => setEditDueDate(e.target.value)}
                        className="ml-2 rounded border border-gray-300 px-2 py-1 text-sm"
                        disabled={updating}
                      />
                    </label>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={handleSaveEdit}
                      disabled={updating}
                      className="rounded bg-torch-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-torch-primary-dark disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(false);
                        setEditNotes(invoice.notes || '');
                        setEditDueDate(invoice.due_date || '');
                      }}
                      disabled={updating}
                      className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="whitespace-pre-wrap text-sm text-gray-900">{invoice.notes || '—'}</p>
              )}
            </div>

            {canUpdate && (
              <div className="mt-6 border-t border-gray-200 pt-4">
                <div className="mb-2 text-sm font-medium text-gray-700">Update status</div>
                <div className="flex flex-wrap gap-2">
                  {invoice.status !== 'sent' && (
                    <button
                      type="button"
                      disabled={updating}
                      onClick={() => handleStatus('sent')}
                      className="rounded border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                    >
                      Mark as sent
                    </button>
                  )}
                  {invoice.status !== 'paid' && (
                    <button
                      type="button"
                      disabled={updating}
                      onClick={() => handleStatus('paid')}
                      className="rounded border border-green-200 bg-green-50 px-3 py-1.5 text-sm text-green-800 hover:bg-green-100 disabled:opacity-50"
                    >
                      Mark as paid
                    </button>
                  )}
                  {invoice.status !== 'overdue' && invoice.status !== 'paid' && (
                    <button
                      type="button"
                      disabled={updating}
                      onClick={() => handleStatus('overdue')}
                      className="rounded border border-red-200 bg-red-50 px-3 py-1.5 text-sm text-red-800 hover:bg-red-100 disabled:opacity-50"
                    >
                      Mark overdue
                    </button>
                  )}
                  {isAdmin && invoice.status !== 'draft' && (
                    <button
                      type="button"
                      disabled={updating}
                      onClick={() => handleStatus('draft')}
                      className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Reset to draft
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      disabled={updating}
                      onClick={handleDelete}
                      className="rounded border border-red-200 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  )}
                </div>
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
              </div>
            )}
          </PageCard>
        </>
      )}
    </Layout>
  );
};

export default InvoiceDetail;
